import { useState, useEffect, useCallback } from "react";
import wishlistService, {
  WishlistCourse,
} from "../services/api/wishlistService";
import { useAuth } from "../contexts/AuthContext";

interface UseWishlistReturn {
  wishlistItems: WishlistCourse[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  } | null;
  addToWishlist: (courseId: string) => Promise<boolean>;
  removeFromWishlist: (courseId: string) => Promise<boolean>;
  isInWishlist: (courseId: string) => boolean;
  fetchWishlist: (page?: number, limit?: number) => Promise<void>;
  refreshWishlist: () => Promise<void>;
}

export const useWishlist = (): UseWishlistReturn => {
  const [wishlistItems, setWishlistItems] = useState<WishlistCourse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<{
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  } | null>(null);

  const { user, isAuthenticated } = useAuth();

  const fetchWishlist = useCallback(
    async (page = 1, limit = 20) => {
      if (!isAuthenticated || !user) {
        setWishlistItems([]);
        setPagination(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await wishlistService.getUserWishlist({ page, limit });

        if (response.success) {
          setWishlistItems(response.data.wishlistItems);
          setPagination(response.data.pagination);
        } else {
          throw new Error(response.message);
        }
      } catch (err: any) {
        setError(err.message || "Failed to fetch wishlist");
        setWishlistItems([]);
        setPagination(null);
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated, user]
  );

  const addToWishlist = useCallback(
    async (courseId: string): Promise<boolean> => {
      if (!isAuthenticated || !user) {
        setError("You must be logged in to add courses to wishlist");
        return false;
      }

      try {
        setError(null);
        const response = await wishlistService.addToWishlist(courseId);

        if (response.success) {
          // Add the new item to the current wishlist
          setWishlistItems((prev) => [response.data, ...prev]);
          return true;
        } else {
          throw new Error(response.message);
        }
      } catch (err: any) {
        setError(err.message || "Failed to add course to wishlist");
        return false;
      }
    },
    [isAuthenticated, user]
  );

  const removeFromWishlist = useCallback(
    async (courseId: string): Promise<boolean> => {
      if (!isAuthenticated || !user) {
        setError("You must be logged in to remove courses from wishlist");
        return false;
      }

      try {
        setError(null);
        const response = await wishlistService.removeFromWishlist(courseId);

        if (response.success) {
          // Remove the item from the current wishlist
          setWishlistItems((prev) =>
            prev.filter((item) => item.courseId !== courseId)
          );
          return true;
        } else {
          throw new Error(response.message);
        }
      } catch (err: any) {
        setError(err.message || "Failed to remove course from wishlist");
        return false;
      }
    },
    [isAuthenticated, user]
  );

  const isInWishlist = useCallback(
    (courseId: string): boolean => {
      return wishlistItems.some((item) => item.courseId === courseId);
    },
    [wishlistItems]
  );

  const refreshWishlist = useCallback(async () => {
    if (pagination) {
      await fetchWishlist(pagination.page, pagination.limit);
    } else {
      await fetchWishlist();
    }
  }, [fetchWishlist, pagination]);

  // Fetch wishlist when user authentication state changes
  useEffect(() => {
    if (isAuthenticated && user && user.role !== "ADMIN") {
      fetchWishlist();
    } else {
      setWishlistItems([]);
      setPagination(null);
    }
  }, [isAuthenticated, user, fetchWishlist]);

  return {
    wishlistItems,
    loading,
    error,
    pagination,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    fetchWishlist,
    refreshWishlist,
  };
};
